import {FormControl, FormField, Separator} from 'platform/components';
import {Grid, GridItem, Heading, VStack} from 'platform/foundation';

import {GetSeriesListApiResponse} from '@dms/api/core';
import {BaseServiceOrderVariant, GetDirectSaleVariantsResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {getOptionsFromSeries} from '@dms/settings/shared';
import {useDocumentTemplates} from '@dms/shared';

import {Nullish, TestIdProps, suffixTestId} from 'shared/web';

import {WarehouseDetailForm} from '../types/WarehouseDetailForm';
import {AllowDirectSales} from './AllowDirectSales';
import {AllowServiceOrderIssue} from './AllowServiceOrderIssue';

interface DocumentsFormProps extends TestIdProps {
  control: FormControl<WarehouseDetailForm>;
  series: GetSeriesListApiResponse | Nullish;
  serviceOrderVariants: BaseServiceOrderVariant[] | Nullish;
  directSaleVariants: GetDirectSaleVariantsResponse | Nullish;
}

export function DocumentsForm(props: DocumentsFormProps) {
  const [getTemplates] = useDocumentTemplates();

  const {options: receiveNoteTemplateOptions} = getTemplates('warehouse-receipt-html');
  const {options: creditNoteTemplateOptions} = getTemplates('warehouse-corrective-receipt-html');
  const {options: issueNoteTemplateOptions} = getTemplates('warehouse-issue-html');
  const {options: issueNoteCorrectionTemplateOptions} = getTemplates(
    'warehouse-corrective-issue-html'
  );

  return (
    <VStack spacing={6}>
      <Heading size={3}>{i18n.t('entity.warehouse.labels.documents')}</Heading>
      <Grid columns={4}>
        <GridItem span={1}>
          <FormField
            name="receiveNoteSeriesId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.receiveNoteSeries')}
            options={getOptionsFromSeries(props.series, 'warehouse/receipt')}
            control={props.control}
            isRequired
            isNotClearable
            data-testid={suffixTestId('inputs.receiveNoteSeriesId', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            name="receiveNoteTemplateId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.receiveNoteTemplate')}
            options={receiveNoteTemplateOptions}
            control={props.control}
            data-testid={suffixTestId('inputs.receiveNoteTemplateId', props)}
          />
        </GridItem>
      </Grid>

      <Grid columns={4}>
        <GridItem span={1}>
          <FormField
            name="creditNoteSeriesId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.creditNoteSeries')}
            options={getOptionsFromSeries(props.series, 'warehouse/corrective-receipt')}
            control={props.control}
            isRequired
            isNotClearable
            data-testid={suffixTestId('inputs.creditNoteSeriesId', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            name="creditNoteTemplateId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.creditNoteTemplate')}
            options={creditNoteTemplateOptions}
            control={props.control}
            data-testid={suffixTestId('inputs.creditNoteTemplateId', props)}
          />
        </GridItem>
      </Grid>

      <Grid columns={4}>
        <GridItem span={1}>
          <FormField
            name="issueNoteSeriesId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.issueNoteSeries')}
            options={getOptionsFromSeries(props.series, 'warehouse/dispense')}
            control={props.control}
            isRequired
            isNotClearable
            data-testid={suffixTestId('inputs.issueNoteSeriesId', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            name="issueNoteTemplateId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.issueNoteTemplate')}
            options={issueNoteTemplateOptions}
            control={props.control}
            data-testid={suffixTestId('inputs.issueNoteTemplateId', props)}
          />
        </GridItem>
      </Grid>

      <Grid columns={4}>
        <GridItem span={1}>
          <FormField
            name="issueNoteCorrectionSeriesId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.issueNoteCorrectionSeries')}
            options={getOptionsFromSeries(props.series, 'warehouse/issue-note-correction')}
            control={props.control}
            isRequired
            isNotClearable
            data-testid={suffixTestId('inputs.issueNoteCorrectionSeriesId', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            name="issueNoteCorrectionTemplateId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.issueNoteCorrectionTemplate')}
            options={issueNoteCorrectionTemplateOptions}
            control={props.control}
            data-testid={suffixTestId('inputs.issueNoteCorrectionTemplateId', props)}
          />
        </GridItem>
      </Grid>

      <AllowServiceOrderIssue
        control={props.control}
        serviceOrderVariants={props.serviceOrderVariants}
        data-testid={suffixTestId('inputs.allowServiceOrderIssue', props)}
      />

      <Grid columns={2}>
        <Separator spacing={0} />
      </Grid>

      <AllowDirectSales
        control={props.control}
        directSaleVariants={props.directSaleVariants}
        data-testid={suffixTestId('inputs.allowDirectSales', props)}
      />
    </VStack>
  );
}
